import { useState } from "react";
import { ChevronDown, Eye, FileText, ImageIcon, Loader2, Trash2 } from "lucide-react";

import { cn } from "@/lib/utils";
import { formatDate } from "@/lib/format";
import { useRemoveMyCompanyDocument } from "@/hooks/mutations/useMyCompanyDocumentMutations";
import type { MyCompanyDocument, MyCompanyDocumentSlot } from "@/types/domain";

/**
 * Una categoría de documentos de la empresa y sus archivos. La categoría la
 * decide la IA al leer cada archivo, así que acá no se sube nada: se lista.
 */

const DOCUMENT_STATES: Record<
  MyCompanyDocument["state"],
  { label: string; pill: string }
> = {
  PROCESSING: {
    label: "Leyendo",
    pill: "bg-sky-50 text-sky-700 dark:bg-sky-950/30 dark:text-sky-300",
  },
  FAILED: {
    label: "No se pudo leer",
    pill: "bg-rose-50 text-rose-700 dark:bg-rose-950/30 dark:text-rose-300",
  },
  VALID: {
    label: "Vigente",
    pill: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300",
  },
  EXPIRING: {
    label: "Vence pronto",
    pill: "bg-amber-50 text-amber-700 dark:bg-amber-950/30 dark:text-amber-300",
  },
  EXPIRED: {
    label: "Vencido",
    pill: "bg-rose-50 text-rose-700 dark:bg-rose-950/30 dark:text-rose-300",
  },
};

const SLOT_STATES: Record<
  MyCompanyDocumentSlot["state"],
  { label: string; pill: string }
> = {
  EMPTY: {
    label: "Sin subir",
    pill: "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400",
  },
  COMPLETE: {
    label: "Completo",
    pill: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300",
  },
  ATTENTION: {
    label: "Requiere atención",
    pill: "bg-amber-50 text-amber-700 dark:bg-amber-950/30 dark:text-amber-300",
  },
};

const formatBytes = (bytes: number) =>
  bytes >= 1048576
    ? `${(bytes / 1048576).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;

function StatePill({ label, pill }: { label: string; pill: string }) {
  return (
    <span
      className={cn(
        "whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider",
        pill,
      )}
    >
      {label}
    </span>
  );
}

/** Un archivo: qué es, cuándo se subió, hasta cuándo vale y en qué va. */
export function CompanyDocumentRow({
  document,
  canManage,
}: {
  document: MyCompanyDocument;
  canManage: boolean;
}) {
  const [confirming, setConfirming] = useState(false);
  const remove = useRemoveMyCompanyDocument();
  const state = DOCUMENT_STATES[document.state];
  const isImage = /\.(jpe?g|png)$/i.test(document.fileName);
  const Icon = isImage ? ImageIcon : FileText;

  return (
    <div className="flex items-center gap-3 rounded-xl border bg-card px-3 py-2.5">
      <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/5 text-primary">
        {document.state === "PROCESSING" ? (
          <Loader2 className="size-4 animate-spin" />
        ) : (
          <Icon className="size-4" />
        )}
      </div>

      <div className="min-w-0 flex-1">
        <div
          className="truncate text-sm font-semibold text-slate-800 dark:text-slate-100"
          title={document.fileName}
        >
          {document.title}
        </div>
        <div className="truncate text-[11px] text-slate-400">
          {formatBytes(document.bytes)} · subido {formatDate(document.uploadedAt)}
          {document.expiresAt &&
            ` · ${document.state === "EXPIRED" ? "venció" : "vence"} ${formatDate(document.expiresAt)}`}
        </div>
      </div>

      <StatePill {...state} />

      {confirming ? (
        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            onClick={() => remove.mutate({ documentId: document.id })}
            disabled={remove.isPending}
            className="cursor-pointer rounded-md bg-rose-600 px-2 py-1 text-[11px] font-semibold text-white hover:bg-rose-700 disabled:opacity-60"
          >
            {remove.isPending ? "Quitando…" : "Quitar"}
          </button>
          <button
            type="button"
            onClick={() => setConfirming(false)}
            className="cursor-pointer rounded-md px-2 py-1 text-[11px] font-semibold text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            Cancelar
          </button>
        </div>
      ) : (
        <div className="flex shrink-0 items-center gap-1 text-slate-400">
          {document.url && (
            <a
              href={document.url}
              target="_blank"
              rel="noreferrer"
              title="Ver documento"
              className="cursor-pointer rounded-md p-1.5 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800"
            >
              <Eye className="size-4" />
            </a>
          )}
          {canManage && (
            <button
              type="button"
              onClick={() => setConfirming(true)}
              title="Quitar de la ficha"
              className="cursor-pointer rounded-md p-1.5 hover:bg-slate-100 hover:text-rose-600 dark:hover:bg-slate-800"
            >
              <Trash2 className="size-4" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/** Una categoría plegable. Se abre sola si algo en ella pide atención. */
export function CompanyDocumentSection({
  title,
  hint,
  state,
  documents,
  canManage,
}: {
  title: string;
  hint: string;
  state: MyCompanyDocumentSlot["state"] | null;
  documents: MyCompanyDocument[];
  canManage: boolean;
}) {
  const [open, setOpen] = useState(state === "ATTENTION");

  return (
    <div className="rounded-2xl border border-slate-100 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        disabled={documents.length === 0}
        className="flex w-full cursor-pointer items-center gap-3 p-5 text-left disabled:cursor-default"
      >
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-bold text-slate-800 dark:text-slate-100">
              {title}
            </span>
            {state && <StatePill {...SLOT_STATES[state]} />}
          </div>
          <div className="mt-0.5 text-xs text-slate-400">{hint}</div>
        </div>
        <span className="shrink-0 text-xs text-slate-500">
          {documents.length === 0
            ? "—"
            : `${documents.length} ${documents.length === 1 ? "archivo" : "archivos"}`}
        </span>
        {documents.length > 0 && (
          <ChevronDown
            className={cn(
              "size-4 shrink-0 text-slate-400 transition-transform",
              open && "rotate-180",
            )}
          />
        )}
      </button>

      {open && documents.length > 0 && (
        <div className="flex flex-col gap-2 px-5 pb-5">
          {documents.map((document) => (
            <CompanyDocumentRow
              key={document.id}
              document={document}
              canManage={canManage}
            />
          ))}
        </div>
      )}
    </div>
  );
}
